import axios from "axios";
import type {
  LoginCredentials,
  CreateUserData,
  UpdateUserData,
  CreateShipmentData,
  UpdateShipmentData,
  AddCommentPayload,
  Column,
  ServiceSnapshot,
  CreateShipmentGroupData,
  UpdateShipmentGroupData,
  AddShipmentsToGroupData,
  RemoveShipmentsFromGroupData,
  ReorderShipmentsData,
} from "../types/index";
import type {
  ContainerLineAttachment,
  ContainerOrderFilters,
  ContainerOrderFormData,
  ContainerLineFormData,
  GenerateLinesPayload,
} from "../types/containerOrders";
import type {
  ContainerMessage,
  ContainerMessagePage,
  ContainerMessageTemplateKey,
  ContainerMessaging,
} from "../types/containerMessages";
import type { PairedTruck, TruckDevice } from "../types/devices";
import type { GroupTruck, TruckGroup } from "../types/truckGroups";
import type { User } from "../types/index";
import type { NotificationFilters } from "../types/notifications";
import type { CalendarResponse } from "../types/calendar";
import type {
  PlanningBoard,
  PlanningMapPage,
  PlanningMapRoute,
  PlanningPoolPage,
} from "../types/planning";
import type { TruckOption, TruckReport } from "../types/containerReports";
import type { IntakeSortKey } from "../types/containerOrderIntake";
import type { Handler } from "../types/handler";
import { isSessionRevoked, notifySessionRevoked } from "../utils/sessionEvents";
import {
  getDeviceToken,
  clearDriverSession,
  clearPairing,
  isPaired,
  DEVICE_ROUTE,
} from "../utils/deviceSession";

type ResourceType =
  | "partners"
  | "addresses"
  | "vehicles"
  | "services"
  | "cost-services";

const isProduction = import.meta.env.MODE;


export const getApiBaseURL = () => {
  const parts = window.location.hostname.split(".");
  const subdomain = parts.length > 2 ? parts[0] : "default";
  if (import.meta.env.MODE === "production") {
    return import.meta.env.VITE_BACKEND_BASE_URL;
  } else {
    return `http://${subdomain}.flowcargo.local:5000/api`; // Tenant portal
  }
};

// Create axios instance
const api = axios.create({
  baseURL: getApiBaseURL(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Important for handling cookies (refresh token)
});

const publicApi = axios.create({
  baseURL: getApiBaseURL(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false,
});

// Request interceptor to add auth token
publicApi.interceptors.request.use(
  (config) => {
    if (isProduction) {
      const subdomain = window.location.hostname.split(".")[0];
      config.headers["x-subdomain"] = subdomain;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const appendFormData = (formData: FormData, data: any, parentKey = "") => {
  if (data === null || data === undefined || data === "") return;

  // File-bearing keys are appended as `key[]` repeats rather than walked as
  // objects.
  //
  // NOTE: this helper drops "", null and undefined, so it cannot express a
  // cleared field — an emptied value never reaches the API and the stored one
  // survives the save. Container orders post JSON instead for that reason (see
  // `containerOrderRequest`); the shipment endpoints still use this.
  if (
    (parentKey === "attachments" || parentKey === "customsAttachments") &&
    Array.isArray(data)
  ) {
    data.forEach((file) => {
      if (file instanceof File) {
        formData.append(`${parentKey}[]`, file);
      }
    });
    return;
  }

  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      appendFormData(formData, item, `${parentKey}[${index}]`);
    });
  } else if (typeof data === "object" && !(data instanceof File)) {
    Object.entries(data).forEach(([key, value]) => {
      const newKey = parentKey ? `${parentKey}[${key}]` : key;
      appendFormData(formData, value, newKey);
    });
  } else {
    formData.append(parentKey, data);
  }
};

/**
 * The device endpoints that run without a user session.
 *
 * They are the way *into* a session, or the way out of one, so a 401 from any of
 * them means "that credential was wrong" — never "your token expired". The
 * generic recovery below must not touch them: there is nothing to refresh, the
 * refresh fails, and the tablet is thrown to `/login`, a screen with no keyboard
 * in reach and no password anybody in the yard knows.
 *
 * That is not hypothetical tidiness. Every mistyped PIN went through it: wrong
 * PIN → 401 → refresh → fail → credentials cleared → `/login`, with the keypad's
 * own "that PIN is not recognised" never getting a chance to render.
 *
 * Listed explicitly rather than matched as `/devices`, because `/devices` is
 * also the office's management list — an admin with an expired token there
 * *should* refresh and retry like anywhere else.
 */
const DEVICE_SESSION_ENDPOINTS = [
  "/devices/pair",
  "/devices/context",
  "/devices/pin-login",
  "/devices/pin-logout",
];

const isDeviceSessionEndpoint = (url?: string) =>
  typeof url === "string" &&
  DEVICE_SESSION_ENDPOINTS.some((path) => url.includes(path));

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const subdomain = window.location.hostname.split(".")[0];
    const isAdminPortal = subdomain === "admin";

    // Use appropriate token based on portal
    const token = isAdminPortal
      ? localStorage.getItem("superAdminToken")
      : localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (isProduction) {
      config.headers["x-subdomain"] = subdomain;
    }

    /**
     * The truck device's own secret, on every request this tablet makes.
     *
     * Sent alongside the driver's bearer token rather than instead of it,
     * because the two answer different questions: the bearer says who is signed
     * in, this says which truck they are signed in *on*. The server scopes every
     * container query by the truck it reads off this header's device record, so
     * a request without it can be a perfectly valid driver login and still have
     * no work to show.
     *
     * Harmless on a browser that has never been paired — `getDeviceToken`
     * returns null and the header is simply absent, which is every dispatcher
     * and every driver on their own phone.
     */
    const deviceToken = getDeviceToken();
    if (deviceToken) {
      config.headers["x-device-token"] = deviceToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Single-session: the account signed in elsewhere. Refreshing would fail
    // too (the refresh token carries the same dead session id), so stop here
    // and let the modal explain it instead of bouncing to /login silently.
    if (isSessionRevoked(error)) {
      notifySessionRevoked(error.response?.data?.message);
      return Promise.reject(error);
    }

    /**
     * A truck device's session ending is not a logout, and must not be treated
     * as one.
     *
     * Two distinct endings arrive here, and the difference is what the tablet
     * has to forget:
     *
     *   DEVICE_SESSION_ENDED  the shift is over — signed out, timed out, or
     *                         another driver took the truck. The pairing is
     *                         still good; the screen goes back to the keypad.
     *   DEVICE_UNPAIRED       the server no longer recognises this device at
     *                         all. The stored secret is dead weight, so it goes,
     *                         and the screen asks for a new pairing code.
     *
     * Neither belongs at `/login`. A tablet bolted into a cab has no email and
     * no password to offer, so sending it to the password form would strand it
     * on a screen nobody in the yard can get past.
     *
     * `DEVICE_SESSION_REQUIRED` is deliberately *not* handled here. It means the
     * caller is signed in with a password and asked for work that belongs to a
     * truck — nothing is broken, nothing should be thrown away, and the driver
     * should not be moved anywhere. Treating it like the two above would clear a
     * perfectly good pairing off the tablet the driver happens to be holding.
     */
    const deviceCode = error.response?.data?.code;
    if (deviceCode === "DEVICE_SESSION_ENDED" || deviceCode === "DEVICE_UNPAIRED") {
      clearDriverSession();
      if (deviceCode === "DEVICE_UNPAIRED") clearPairing();

      if (!window.location.pathname.startsWith(DEVICE_ROUTE)) {
        window.location.href = DEVICE_ROUTE;
      }
      return Promise.reject(error);
    }

    /**
     * A rejected PIN or pairing code is the caller's answer, not our problem.
     *
     * Returned to the page untouched: no refresh, no cleared credentials, no
     * navigation. The keypad and the pairing form both render the server's own
     * message, which is the only place that can say something useful about which
     * six digits or which eight characters were wrong.
     */
    if (isDeviceSessionEndpoint(originalRequest?.url)) {
      return Promise.reject(error);
    }

    /**
     * Rate limited — which is not a session problem, and must not be treated
     * as one.
     *
     * It falls through to the 401/403 branch below otherwise only by accident
     * of status, but the cost of getting this wrong is high enough to be worth
     * saying out loud: a driver told "too many requests" while confirming a
     * step has a working session, a working truck and a job in front of them.
     * Clearing their token and bouncing them to a sign-in form would turn a
     * wait of a few seconds into a PIN re-entry in a cab.
     *
     * So it is passed straight through to whoever made the call, with the
     * server's own message, to be shown as the failure it is. The caller's
     * existing error toast then says something true and temporary rather than
     * "could not save".
     *
     * `Retry-After` is read where the server sent one, because "try again in a
     * minute" is actionable and "too many requests" is not.
     */
    if (error.response?.status === 429) {
      const after = Number(error.response.headers?.["retry-after"]);
      if (Number.isFinite(after) && after > 0) {
        (error as { retryAfterSeconds?: number }).retryAfterSeconds = after;
      }
      return Promise.reject(error);
    }

    // Prevent retry loops
    if (!originalRequest._retry) {
      originalRequest._retry = true;

      // Only try refresh if it's NOT the refresh-token request itself
      if (
        error.response?.status === 401 &&
        !originalRequest.url.includes("/auth/refresh-token") &&
        !originalRequest.url.includes("/superadmin/auth/refresh")
      ) {
        // DETECT PORTAL TYPE
        const subdomain = window.location.hostname.split(".")[0];
        const isAdminPortal = subdomain === "admin";

        try {
          if (isAdminPortal) {
            // SUPERADMIN TOKEN REFRESH
            const res = await api.post("/superadmin/auth/refresh");
            const { token } = res.data.data;

            localStorage.setItem("superAdminToken", token);
            originalRequest.headers.Authorization = `Bearer ${token}`;

            return api(originalRequest);
          } else {
            // TENANT TOKEN REFRESH (existing logic)
            const res = await api.post("/auth/refresh-token");
            const { token } = res.data.data;

            localStorage.setItem("token", token);
            originalRequest.headers.Authorization = `Bearer ${token}`;

            return api(originalRequest);
          }
        } catch (refreshError: any) {
          if (isSessionRevoked(refreshError)) {
            notifySessionRevoked(refreshError.response?.data?.message);
            return Promise.reject(refreshError);
          }

          console.warn("Refresh failed, logging out");

          // CLEAR APPROPRIATE TOKENS BASED ON PORTAL
          if (isAdminPortal) {
            localStorage.removeItem("superAdminToken");
            localStorage.removeItem("superAdmin");
          } else {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }

          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      }
    }

    // IF ALREADY RETRIED OR OTHER 401s/403s, LOGOUT
    if (error.response?.status === 401 || error.response?.status === 403) {
      const subdomain = window.location.hostname.split(".")[0];
      const isAdminPortal = subdomain === "admin";

      if (isAdminPortal) {
        localStorage.removeItem("superAdminToken");
        localStorage.removeItem("superAdmin");
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }

      /**
       * A paired tablet goes back to its keypad, never to the password form.
       *
       * The branch above catches the failures the server labels; this one is the
       * catch-all for everything it does not — an expired token whose refresh
       * also failed, most often. On a phone that lands the user at `/login`,
       * which is right. On a screen bolted into a cab there is no email address
       * to type, so `/login` is a dead end and `/truck` is the same recovery the
       * driver would make anyway: enter the PIN again.
       */
      window.location.href = isPaired() ? DEVICE_ROUTE : "/login";
    }

    return Promise.reject(error);
  },
);

// SuperAdmin Auth API
export const superAdminAuthAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post("/superadmin/auth/login", credentials),

  refreshToken: () => api.post("/superadmin/auth/refresh"),

  logout: () => api.post("/superadmin/auth/logout"),

  getProfile: () => api.get("/superadmin/auth/profile"),

  updateProfile: (data: any) => api.put("/superadmin/auth/profile", data),
};

// SuperAdmin Company Management API
export const superAdminCompanyAPI = {
  getAllCompanies: (params?: any) =>
    api.get("/superadmin/companies", { params }),

  getCompanyById: (id: string) => api.get(`/superadmin/companies/${id}`),

  createCompany: (data: any) => api.post("/superadmin/companies", data),

  updateCompany: (id: string, data: any) =>
    api.put(`/superadmin/companies/${id}`, data),

  /**
   * Replaces the whole module set rather than toggling one key: two admins
   * editing the same company would otherwise interleave their toggles into a
   * set neither of them chose. `updatedAt` carries the optimistic-concurrency
   * check the backend uses to reject a stale edit outright.
   */
  updateCompanyModules: (
    id: string,
    enabledModules: string[],
    updatedAt?: string,
  ) =>
    api.put(`/superadmin/companies/${id}/modules`, {
      enabledModules,
      updatedAt,
    }),

  updateCompanyStatus: (id: string, status: "active" | "inactive") =>
    api.patch(`/superadmin/companies/${id}/status`, { status }),

  deleteCompany: (id: string, confirmSubdomain: string) =>
    api.delete(`/superadmin/companies/${id}`, {
      data: { confirmSubdomain },
    }),

  getCompanyUsers: (id: string, params?: any) =>
    api.get(`/superadmin/companies/${id}/users`, { params }),

  getPlatformStats: () => api.get("/superadmin/companies/platform/stats"),

  getCountries: () => api.get("/superadmin/companies/countries"),

  reprovisionDns: (id: string) =>
    api.post(`/superadmin/companies/${id}/reprovision-dns`),
};

// Tenant API's

// Auth API
export const authAPI = {
  login: (credentials: LoginCredentials) =>
    api.post("/auth/login", credentials),

  refreshToken: () => api.post("/auth/refresh-token"),

  logout: () => api.post("/auth/logout"),

  getMe: () => api.get("/auth/me"),

  forgotPassword: (email: string) =>
    api.post("/auth/forgot-password", { email }),

  resetPassword: (token: string, password: string, confirmPassword: string) =>
    api.post("/auth/reset-password", { token, password, confirmPassword }),

  setPassword: (token: string, password: string, confirmPassword: string) =>
    api.post("/auth/activate", { token, password, confirmPassword }),
};

// User API
export const userAPI = {
  getUsers: (params?: any) => api.get("/users", { params }),

  /**
   * The dispatchers a transport can be handed to.
   *
   * Its own endpoint rather than `getUsers` with a filter, because `getUsers`
   * excludes the caller — and "Mine" is the entry this roster exists for. It is
   * also unpaged: a tenant has a dozen office staff and the consumer is a menu.
   */
  getHandlers: () =>
    api.get<{ data: { handlers: Handler[] } }>("/users/handlers"),

  getUser: (id: string) => api.get(`/users/${id}`),

  createUser: (userData: CreateUserData) => api.post("/users", userData),

  updateUser: (id: string, userData: UpdateUserData) =>
    api.put(`/users/${id}`, userData),

  updateProfile: (userData: UpdateUserData) => api.put("/users", userData),

  deleteUser: (id: string) => api.delete(`/users/${id}`),

  toggleUserStatus: (id: string, status: string) =>
    api.patch(`/users/${id}/status`, status),

  resendInvite: (id: string) => api.post(`/users/${id}/resend-invitation`),
  deleteMultipleUsers: async (ids: string[]) => {
    return api.delete("/users", {
      data: { userIds: ids },
    });
  },
};

export const driverPinAPI = {
  /**
   * Issue one driver a new truck PIN and email it.
   *
   * Also the only way to give a PIN to a driver who has never had one — every
   * account created before truck devices existed. There is no "look up their
   * PIN": only hashes are stored, so recovering one is impossible by
   * construction and issuing a new one is the whole of the answer.
   *
   * Silently retires whatever PIN the driver had, so the confirmation in front
   * of it is not ceremony.
   */
  /**
   * Read a driver's PIN back.
   *
   * Admin-only and audit-logged server-side. Can legitimately fail for reasons
   * that are not errors — no PIN yet, viewing not enabled on this deployment, or
   * a PIN issued before readable copies existed — each with its own `code` so
   * the caller can offer the right next step instead of a generic failure.
   */
  reveal: (userId: string) =>
    api.get<{ message: string; data: { pin: string; driver: { _id: string; email: string } } }>(
      `/users/${userId}/driver-pin`,
    ),

  /**
   * Email the driver the PIN they already have.
   *
   * A true resend: the same six digits, so a PIN written down somewhere still
   * works afterwards. `reissued` comes back true when there was nothing readable
   * to resend and a new one had to be issued instead — the message says so, and
   * the caller should refetch because the driver's PIN has changed.
   */
  send: (userId: string) =>
    api.post<{ message: string; data: { user: User; reissued: boolean } }>(
      `/users/${userId}/driver-pin/send`,
    ),

  reissue: (userId: string) =>
    // `message` is typed alongside `data` because the server's is the better
    // sentence here — it names the address the PIN actually went to, which is
    // the one thing an admin wants confirmed.
    api.post<{ message: string; data: { user: User } }>(
      `/users/${userId}/driver-pin`,
    ),

  /**
   * Issue PINs to every driver who has not got one.
   *
   * The migration button. Skips drivers who already have one rather than
   * rotating them — this fills gaps, and quietly changing a working credential
   * would strand a driver at a gate.
   *
   * Reports each driver individually because a partial failure is the expected
   * outcome: one bad address in a fleet must not decide the fate of the rest,
   * and the button can be pressed again for whoever is left.
   */
  issueMissing: () =>
    api.post<{
      // The server counts the two outcomes into one sentence; re-deriving it on
      // the client would be a second place for "3 sent, 1 failed" to be phrased.
      message: string;
      data: {
        issued: { _id: string; email: string }[];
        failed: { _id: string; email: string }[];
      };
    }>("/users/driver-pins"),
};

export const dashboardAPI = {
  getSummary: (params?: { days?: number }) =>
    api.get("/dashboard/summary", { params }),
};

// Shipment API
export const shipmentAPI = {
  getShipments: (params?: any) => api.get("/shipments", { params }),

  getShipmentsForGroups: (params?: any) =>
    api.get("/shipments/availableShipmentForGroups", { params }),

  getShipment: (id: string) => api.get(`/shipments/${id}`),

  getNextShipmentId: () => api.get("/shipments/preview-next-shipment-id"),

  getShipmentXML: (id: string) =>
    api.get(`/shipments/${id}/generate-xml`, {
      responseType: "blob",
    }),
  createWayBill: (id: string) => api.post(`/shipments/${id}/generate-waybill`),

  createShipment: (shipmentData: CreateShipmentData) => {
    const formData = new FormData();

    appendFormData(formData, shipmentData);

    return api.post("/shipments", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  getShipmentCopyData: (id: string) => api.get(`/shipments/${id}/copy`),

  // Post-completion service management — works on completed/locked shipments.
  addShipmentService: (id: string, service: Record<string, unknown>) =>
    api.post(`/shipments/${id}/services`, service),

  deleteShipmentService: (id: string, serviceId: string) =>
    api.delete(`/shipments/${id}/services/${serviceId}`),

  assignShipmentToDriverApp: (id: string) =>
    api.post(`/shipments/${id}/assign-to-driver`),

  removeShipmentFromDriverApp: (id: string) =>
    api.post(`/shipments/${id}/remove-from-driver`),

  updateShipment: (id: string, shipmentData: UpdateShipmentData) => {
    const formData = new FormData();

    appendFormData(formData, shipmentData);

    return api.put(`/shipments/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  deleteShipment: (id: string) => api.delete(`/shipments/${id}`),

  deleteMultipleShipments: async (ids: string[]) => {
    return api.delete("/shipments", {
      data: { shipmentIds: ids },
    });
  },
  downloadAttachment: async (shipmentId: string, attachmentId: string) => {
    return api.get(
      `/shipments/${shipmentId}/attachments/${attachmentId}/download`,
      {
        responseType: "blob",
      },
    );
  },
  sendCmrAttachments: (data: FormData) =>
    api.post("/shipments/send-cmr-attachments", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  getShipmentComments: (
    shipmentId: string,
    params?: { page?: number; limit?: number },
  ) =>
    api.get(`/shipments/${shipmentId}/comments`, {
      params,
    }),

  addShipmentComment: (shipmentId: string, data: AddCommentPayload) =>
    api.post(`/shipments/${shipmentId}/comments`, data),

  deleteShipmentComment: (shipmentId: string, commentId: string) =>
    api.delete(`/shipments/${shipmentId}/comments/${commentId}`),

  toggleAttachmentPublic: (shipmentId: string, attachmentId: string) =>
    api.patch(
      `/shipments/${shipmentId}/attachments/${attachmentId}/toggle-public`,
    ),
  sendDeliveryOtp: (shipmentId: string, recipientEmail: string) =>
    api.post(`/shipments/${shipmentId}/send-otp`, { recipientEmail }),

  // Signed Documents (system side) — permanent, immutable. Upload + download only.
  uploadSignedDocuments: (shipmentId: string, formData: FormData) =>
    api.post(`/shipments/${shipmentId}/signed-documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  downloadSignedDocument: (
    shipmentId: string,
    documentId: string,
    preview = false,
  ) =>
    api.get(
      `/shipments/${shipmentId}/signed-documents/${documentId}/download`,
      { responseType: "blob", params: preview ? { preview: 1 } : undefined },
    ),
};

// Shipment Group API
export const shipmentGroupAPI = {
  getShipmentGroups: (params?: any) => api.get("/shipment-groups", { params }),

  getShipmentGroup: (id: string) => api.get(`/shipment-groups/${id}`),

  createShipmentGroup: (groupData: CreateShipmentGroupData) =>
    api.post("/shipment-groups", groupData),

  updateShipmentGroup: (id: string, groupData: UpdateShipmentGroupData) =>
    api.put(`/shipment-groups/${id}`, groupData),

  addShipmentsToGroup: (id: string, data: AddShipmentsToGroupData) =>
    api.post(`/shipment-groups/${id}/shipments/add`, data),

  removeShipmentsFromGroup: (id: string, data: RemoveShipmentsFromGroupData) =>
    api.post(`/shipment-groups/${id}/shipments/remove`, data),

  reorderShipments: (id: string, data: ReorderShipmentsData) =>
    api.put(`/shipment-groups/${id}/shipments/reorder`, data),

  assignGroupToDriverApp: (id: string) =>
    api.patch(`/shipment-groups/${id}/assign-driver-app`),

  removeGroupFromDriverApp: (id: string) =>
    api.patch(`/shipment-groups/${id}/remove-driver-app`),

  deleteShipmentGroup: (id: string) => api.delete(`/shipment-groups/${id}`),

  deleteMultipleShipmentGroups: async (ids: string[]) => {
    return api.delete("/shipment-groups", {
      data: { groupIds: ids },
    });
  },
};

export const publicShipmentAPI = {
  getShipment: (id: string, sessionToken?: string) =>
    publicApi.get(`/shipments/clients-view/${id}`, {
      headers: sessionToken ? { "x-public-session": sessionToken } : {},
    }),
  uploadAttachments: (shipmentId: string, formData: FormData) =>
    publicApi.post(
      `/shipments/clients-view/${shipmentId}/attachments`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),
  requestOtp: (shipmentId: string, email: string) =>
    publicApi.post(`/shipments/clients-view/${shipmentId}/otp/request`, {
      email,
    }),
  verifyOtp: (shipmentId: string, email: string, otp: string) =>
    publicApi.post(`/shipments/clients-view/${shipmentId}/otp/verify`, {
      email,
      otp,
    }),
  submitAccessRequest: (shipmentId: string, email: string) =>
    publicApi.post(`/shipments/clients-view/${shipmentId}/access-request`, {
      email,
    }),
};

export const shipmentAccessAPI = {
  updateAllowedEmails: (
    shipmentId: string,
    allowedEmails: { email: string; role: "view" | "poh_handler" }[],
  ) => api.patch(`/shipments/${shipmentId}/allowed-emails`, { allowedEmails }),

  getAccessRequests: (shipmentId: string) =>
    api.get(`/shipments/${shipmentId}/access-requests`),

  respondToAccessRequest: (
    shipmentId: string,
    requestId: string,
    status: "accepted" | "rejected",
  ) =>
    api.patch(`/shipments/${shipmentId}/access-requests/${requestId}`, {
      status,
    }),
};

export const publicReclamationAPI = {
  fileReclamation: (shipmentId: string, formData: FormData) =>
    publicApi.post(
      `/shipments/clients-view/${shipmentId}/reclamations`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),
  getReclamations: (shipmentId: string, source?: "customer" | "driver") =>
    publicApi.get(`/shipments/clients-view/${shipmentId}/reclamations`, {
      params: source ? { source } : undefined,
    }),
};

export const reclamationAPI = {
  getReclamations: (params?: Record<string, string | number | undefined>) =>
    api.get("/reclamations", { params }),
  getReclamation: (id: string) => api.get(`/reclamations/${id}`),
  updateStatus: (
    id: string,
    data: {
      status: string;
      approvedAmount?: number;
      resolutionNote?: string;
    },
  ) => api.patch(`/reclamations/${id}/status`, data),
  deleteReclamation: (id: string) => api.delete(`/reclamations/${id}`),
};

export const driverReclamationAPI = {
  fileReclamation: (shipmentId: string, formData: FormData) =>
    api.post(`/driver/shipments/${shipmentId}/reclamations`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

export type GeoStamp = {
  lat: number;
  lng: number;
  accuracy?: number;
  capturedAt?: string;
};

const buildSignFormData = (
  data: Record<string, string | undefined>,
  geo: GeoStamp,
  files: File[],
) => {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  fd.append("geo", JSON.stringify(geo));
  files.forEach((f) => fd.append("attachments", f));
  return fd;
};

const multipart = { headers: { "Content-Type": "multipart/form-data" } };

export const pohAPI = {
  consignerSign: (
    shipmentId: string,
    data: { signerName: string; signatureData: string },
    geo: GeoStamp,
    files: File[] = [],
  ) =>
    api.post(
      `/poh/${shipmentId}/pickup/consigner-sign`,
      buildSignFormData(data, geo, files),
      multipart,
    ),

  getPohByShipment: (shipmentId: string) =>
    api.get(`/poh/shipment/${shipmentId}`),

  getDriverSignInfo: (qrToken: string) =>
    publicApi.get(`/poh/driver-sign/${qrToken}`),

  requestPickupDriverOtp: (
    qrToken: string,
    data: { signerName: string; driverEmail: string; identityNumber: string },
  ) => publicApi.post(`/poh/driver-sign/${qrToken}/request-otp`, data),

  verifyPickupDriverOtp: (qrToken: string, otp: string) =>
    publicApi.post(`/poh/driver-sign/${qrToken}/verify-otp`, { otp }),

  driverPickupSign: (
    qrToken: string,
    data: { signatureData: string },
    geo: GeoStamp,
    files: File[] = [],
  ) =>
    publicApi.post(
      `/poh/driver-sign/${qrToken}`,
      buildSignFormData(data, geo, files),
      multipart,
    ),

  customerDeliverySign: (
    shipmentId: string,
    data: { signerName: string; signerEmail: string; signatureData: string },
    geo: GeoStamp,
    files: File[] = [],
    sessionToken?: string,
  ) =>
    publicApi.post(
      `/poh/${shipmentId}/delivery/customer-sign`,
      buildSignFormData(data, geo, files),
      {
        ...multipart,
        headers: {
          ...multipart.headers,
          ...(sessionToken ? { "x-public-session": sessionToken } : {}),
        },
      },
    ),

  getDeliveryPoh: (shipmentId: string) =>
    publicApi.get(`/poh/${shipmentId}/delivery`),

  getDeliveryDriverSignInfo: (qrToken: string) =>
    publicApi.get(`/poh/delivery-driver-sign/${qrToken}`),

  requestDeliveryDriverOtp: (
    qrToken: string,
    data: { signerName: string; driverEmail: string; identityNumber: string },
  ) => publicApi.post(`/poh/delivery-driver-sign/${qrToken}/request-otp`, data),

  verifyDeliveryDriverOtp: (qrToken: string, otp: string) =>
    publicApi.post(`/poh/delivery-driver-sign/${qrToken}/verify-otp`, { otp }),

  driverDeliveryQrSign: (
    qrToken: string,
    data: { signatureData: string },
    geo: GeoStamp,
    files: File[] = [],
  ) =>
    publicApi.post(
      `/poh/delivery-driver-sign/${qrToken}`,
      buildSignFormData(data, geo, files),
      multipart,
    ),

  driverDeliverySign: (
    shipmentId: string,
    data: { signerName: string; identityNumber: string; signatureData: string },
    geo: GeoStamp,
    files: File[] = [],
  ) =>
    api.post(
      `/poh/${shipmentId}/delivery/driver-sign`,
      buildSignFormData(data, geo, files),
      multipart,
    ),
};

export const userSettingsAPI = {
  getShipmentColumns: () => api.get("/settings/shipment-columns"),
  updateShipmentColumns: (columns: Column[]) =>
    api.put("/settings/shipment-columns", { columns: columns }),
  resetShipmentColumns: () => api.post("/settings/shipment-columns/reset"),

  getContainerOrderColumns: () => api.get("/settings/container-order-columns"),
  updateContainerOrderColumns: (columns: Column[]) =>
    api.put("/settings/container-order-columns", { columns }),
  resetContainerOrderColumns: () =>
    api.post("/settings/container-order-columns/reset"),
};

export const companyAPI = {
  getCompany: () => api.get("/companies"),

  getCompanyStats: () => api.get("/companies/stats"),

  getCountries: () => api.get("/companies/countries"),

  // Organisation Settings (admin only)
  updateInvoicingSettings: (data: any) =>
    api.put("/companies/invoicing-settings", data),

  updateInvoiceNumberRange: (data: { start: number; end: number }) =>
    api.put("/companies/invoice-number-range", data),

  uploadInvoiceLogo: (file: File) => {
    const form = new FormData();
    form.append("logo", file);
    return api.post("/companies/invoicing-logo", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export const invoiceAPI = {
  getClientSummary: (clientId: string) =>
    api.get(`/invoices/clients/${clientId}/summary`),

  // Signed Documents available on the invoice's shipments (for the send picker).
  getSignedDocuments: (shipmentIds: string[]) =>
    api.get("/invoices/signed-documents", {
      params: { shipmentIds: shipmentIds.join(",") },
    }),

  // Global "uninvoiced shipments/works" tab source.
  invoiceableItems: (params?: {
    clientId?: string;
    sourceType?: string;
    page?: number;
    limit?: number;
  }) => api.get("/invoices/invoiceable-items", { params }),

  preview: (data: {
    clientId: string;
    shipmentIds: string[];
    workIds: string[];
    /** Containers — one physical box each, resolved to a client via its order. */
    containerLineIds?: string[];
    /** Per-source service-row selection: { [sourceId]: serviceRowIds } */
    serviceSelections?: Record<string, string[]>;
  }) => api.post("/invoices/preview", data),

  list: (params?: any) => api.get("/invoices", { params }),

  /**
   * The listing flattened for a spreadsheet — same filters as `list`, no
   * paging. Returns rows; the workbook is built in the browser (see
   * useInvoiceExport), the way the shipment export does it.
   */
  exportRows: (params?: any) => api.get("/invoices/export", { params }),

  getOne: (id: string) => api.get(`/invoices/${id}`),

  create: (data: any) => api.post("/invoices", data),

  update: (id: string, data: any) => api.patch(`/invoices/${id}`, data),

  lock: (id: string, data: any) => api.post(`/invoices/${id}/lock`, data),

  setStatus: (id: string, status: string) =>
    api.patch(`/invoices/${id}/status`, { status }),

  cancel: (id: string, reason?: string) =>
    api.post(`/invoices/${id}/cancel`, { reason }),

  createCreditNote: (id: string, data: any) =>
    api.post(`/invoices/${id}/credit-note`, data),

  // Fetch the PDF as a blob through axios so the bearer token is attached
  // (a plain <a href> navigation to the auth-protected route would 401).
  getPdfBlob: (id: string) =>
    api.get(`/invoices/${id}/pdf`, { responseType: "blob" }),
};

// SO/PO orders synced from Dynamics 365 Business Central
export const orderAPI = {
  list: (params?: any) => api.get("/orders", { params }),

  getOne: (id: string) => api.get(`/orders/${id}`),

  // LC-supplement fields only (item weights/volumes, internal notes)
  update: (id: string, data: any) => api.put(`/orders/${id}`, data),

  refresh: (id: string, confirmed?: boolean) =>
    api.post(`/orders/${id}/refresh`, { confirmed }),

  setAutoSync: (id: string, enabled: boolean) =>
    api.put(`/orders/${id}/auto-sync`, { enabled }),

  cancel: (id: string, confirmed?: boolean) =>
    api.post(`/orders/${id}/cancel`, { confirmed }),

  dismissDiscrepancy: (id: string) =>
    api.put(`/orders/${id}/dismiss-discrepancy`),

  getShipmentPrefill: (id: string) => api.get(`/orders/${id}/shipment-prefill`),
};

/**
 * Build a container request: one JSON part plus the picked files.
 *
 * Deliberately not `appendFormData`. Flattened multipart carries strings only
 * and drops `""`, `null` and `undefined` alike, so it cannot say "this field
 * was cleared" — a blanked Reference or tare weight simply never reached the
 * API, and the update left the stored value in place. JSON round-trips null,
 * numbers, booleans and empty arrays exactly, which is what an update needs to
 * tell "unchanged" apart from "emptied". The files still travel as their own
 * multipart fields, one per drop zone, so the server keeps knowing which
 * category each one came from.
 *
 * Shared by the order and by one container, which post the same two zones to
 * the same two field names — the container's own save carries its own
 * documents, exactly as the order's carries the order's.
 */
const containerRequest = <
  T extends {
    attachments?: File[];
    customsAttachments?: File[];
    platePhotos?: File[];
  },
>(
  data: T,
) => {
  const { attachments, customsAttachments, platePhotos, ...payload } = data;
  const formData = new FormData();

  formData.append("payload", JSON.stringify(payload));

  (attachments ?? []).forEach((file) => {
    if (file instanceof File) formData.append("attachments[]", file);
  });
  (customsAttachments ?? []).forEach((file) => {
    if (file instanceof File) formData.append("customsAttachments[]", file);
  });
  // The door photograph a reading was imported from. Its own category rather
  // than filed among the paperwork: it answers "where did these figures come
  // from", which is a different question from "what travelled with this box".
  (platePhotos ?? []).forEach((file) => {
    if (file instanceof File) formData.append("platePhotos[]", file);
  });

  return formData;
};

/** The two zones on their own, for a screen that uploads without a form save. */
export const attachmentUploadForm = (files: {
  attachments?: File[];
  customsAttachments?: File[];
  platePhotos?: File[];
}) => containerRequest(files);

/**
 * Container orders — batch container bookings created in LiveCargo.
 * Unrelated to `orderAPI` above, which is the Dynamics 365 SO/PO sync.
 */
export const containerOrderAPI = {
  list: (params?: ContainerOrderFilters) =>
    api.get("/container-orders", { params }),

  getOne: (id: string) => api.get(`/container-orders/${id}`),

  getNextOrderNumber: () =>
    api.get("/container-orders/preview-next-order-number"),

  /**
   * The prefill behind "Copy order" — the order's values minus the ones that
   * belong to that one booking (reference, booking number, its documents).
   * Reads only; the copy is created by the create form's own save.
   */
  getCopyData: (id: string) => api.get(`/container-orders/${id}/copy`),

  create: (data: ContainerOrderFormData) =>
    api.post("/container-orders", containerRequest(data), {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // `updatedAt` is the optimistic-concurrency token captured at load time.
  update: (id: string, data: ContainerOrderFormData & { updatedAt?: string }) =>
    api.put(`/container-orders/${id}`, containerRequest(data), {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  remove: (id: string, updatedAt?: string) =>
    api.delete(`/container-orders/${id}`, { data: { updatedAt } }),


  /**
   * What cancelling this order would actually do — the confirmation dialog's
   * figures.
   *
   * Fetched rather than worked out on the client, because the client holds one
   * page of the batch and the question is about all of it. Answers for an order
   * that cannot be cancelled too (`blocked` carries the reason), so the dialog
   * can explain instead of refusing to open.
   */
  cancelPreview: (id: string) =>
    api.get(`/container-orders/${id}/cancel-preview`),

  // ── Lines ───────────────────────────────────────────────────────────────
  // A line is one physical container. Bulk generation and manual add are both
  // supported: "twelve of the same" and "the thirteenth that turned up late".
  // `search` narrows the batch to the containers matching the list's search
  // box — the same predicate the list used to find the order in the first
  // place, so an order found by a container number opens on that container.
  listLines: (
    orderId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      /**
       * The handler scope, carried down from the list by the same route the
       * search box already travels. The panel is a page of ten, so a batch
       * whose single matching container sits on page two has to be narrowed
       * rather than merely dimmed.
       */
      handler?: string;
    },
  ) => api.get(`/container-orders/${orderId}/lines`, { params }),

  generateLines: (orderId: string, payload: GenerateLinesPayload) =>
    api.post(`/container-orders/${orderId}/lines/generate`, payload),

  createLine: (orderId: string, data: ContainerLineFormData) =>
    api.post(`/container-orders/${orderId}/lines`, data),

  // One container, for its own page. Comes back with the four order fields that
  // page shows read-only, so it costs one request rather than two.
  getLine: (orderId: string, lineId: string) =>
    api.get(`/container-orders/${orderId}/lines/${lineId}`),

  // Multipart like the order's own update: the container's documents are saved
  // by the same press of Save that saves its fields.
  updateLine: (
    orderId: string,
    lineId: string,
    data: ContainerLineFormData & { updatedAt?: string },
  ) =>
    api.put(
      `/container-orders/${orderId}/lines/${lineId}`,
      containerRequest(data),
      { headers: { "Content-Type": "multipart/form-data" } },
    ),

  /**
   * Cancel one container, and put one back.
   *
   * Not `updateLine({ status })` — that shortcut is refused by the API now.
   * Cancelling releases the container's board slots, checks what has been
   * billed, and tells the board and the cab; none of that belongs on the path an
   * ordinary save takes.
   */
  cancelLine: (
    orderId: string,
    lineId: string,
    payload: { reason?: string; updatedAt?: string } = {},
  ) => api.post(`/container-orders/${orderId}/lines/${lineId}/cancel`, payload),

  restoreLine: (orderId: string, lineId: string, updatedAt?: string) =>
    api.post(`/container-orders/${orderId}/lines/${lineId}/restore`, {
      updatedAt,
    }),

  // ── One container's own documents ───────────────────────────────────────
  // Readable by everyone who can see the container, whichever side filed them.
  // Removal is the server's call: a driver's upload is theirs alone.
  downloadLineAttachment: (
    orderId: string,
    lineId: string,
    attachmentId: string,
  ) =>
    api.get(
      `/container-orders/${orderId}/lines/${lineId}/attachments/${attachmentId}/download`,
      { responseType: "blob" },
    ),

  deleteLineAttachment: (
    orderId: string,
    lineId: string,
    attachmentId: string,
  ) =>
    api.delete(
      `/container-orders/${orderId}/lines/${lineId}/attachments/${attachmentId}`,
    ),

  /**
   * ── Charges on one container, outside the form ───────────────────────────
   *
   * The container's own Save handles its charges while it is still running.
   * These two are for after it is not: a completed container refuses form edits
   * — it is a record of what happened — but invoicing happens after the run, and
   * demurrage, waiting time and an extra lift are all discovered once the box is
   * back. So the record stays shut and the charges stay open.
   *
   * `kind` is in the path rather than the body because it is the whole
   * distinction between the two lists: `services` are billable and invoiceable,
   * `cost-services` are internal and never invoiced.
   */
  addLineService: (
    orderId: string,
    lineId: string,
    kind: "services" | "cost-services",
    service: Record<string, unknown>,
  ) =>
    api.post(`/container-orders/${orderId}/lines/${lineId}/${kind}`, service),

  deleteLineService: (
    orderId: string,
    lineId: string,
    kind: "services" | "cost-services",
    serviceId: string,
  ) =>
    api.delete(
      `/container-orders/${orderId}/lines/${lineId}/${kind}/${serviceId}`,
    ),

  deleteLine: (orderId: string, lineId: string) =>
    api.delete(`/container-orders/${orderId}/lines/${lineId}`),

  // Cancel is the only order status a user drives by hand; every other status
  // is derived from the order's lines.
  /**
   * Hand this booking to somebody, or take it back.
   *
   * Its own call rather than a field on `update`, because `update` refuses a
   * cancelled order ("Reopen it first") and a cancelled order's unbilled
   * containers still belong to somebody. `cascade` carries the change down to
   * the containers already generated; the response says how many moved.
   */
  setOrderHandler: (
    orderId: string,
    handlerId: string | null,
    cascade = false,
  ) =>
    api.patch<{
      data: { handler: Handler | null; containersUpdated: number };
      message: string;
    }>(`/container-orders/${orderId}/handler`, { handlerId, cascade }),

  /**
   * Hand one container to somebody.
   *
   * Separate from `updateLine` for a sharper version of the same reason: that
   * endpoint refuses a *completed* container, and a completed container is
   * precisely the one still being invoiced.
   */
  setLineHandler: (orderId: string, lineId: string, handlerId: string | null) =>
    api.patch<{ data: { handler: Handler | null }; message: string }>(
      `/container-orders/${orderId}/lines/${lineId}/handler`,
      { handlerId },
    ),

  cancel: (id: string, updatedAt?: string) =>
    api.post(`/container-orders/${id}/cancel`, { updatedAt }),

  reopen: (id: string, updatedAt?: string) =>
    api.post(`/container-orders/${id}/reopen`, { updatedAt }),

  downloadAttachment: (orderId: string, attachmentId: string) =>
    api.get(
      `/container-orders/${orderId}/attachments/${attachmentId}/download`,
      { responseType: "blob" },
    ),

  deleteAttachment: (orderId: string, attachmentId: string) =>
    api.delete(`/container-orders/${orderId}/attachments/${attachmentId}`),
};

// Third-party integrations (admin only)
export const integrationAPI = {
  getDynamics: () => api.get("/integrations/dynamics"),

  saveDynamics: (data: { tenantId: string; environment: string }) =>
    api.post("/integrations/dynamics", data),

  testDynamics: () => api.post("/integrations/dynamics/test"),

  listDynamicsCompanies: () => api.get("/integrations/dynamics/companies"),

  saveDynamicsCompanies: (
    selectedCompanies: { bcCompanyId: string; name: string; enabled: boolean }[],
  ) => api.put("/integrations/dynamics/companies", { selectedCompanies }),

  setDynamicsEnabled: (enabled: boolean) =>
    api.put("/integrations/dynamics/status", { enabled }),

  disconnectDynamics: () => api.delete("/integrations/dynamics"),

  syncDynamicsNow: () => api.post("/integrations/dynamics/sync"),

  // Oliver — transport orders read from e-mails/documents. The token is
  // write-only: the server only ever returns its last four characters.
  getOliver: () => api.get("/integrations/oliver"),

  saveOliver: (data: { token: string; enabled?: boolean }) =>
    api.post("/integrations/oliver", data),

  /** Tests the token passed, or the saved one when none is. */
  testOliver: (token?: string) =>
    api.post("/integrations/oliver/test", token ? { token } : {}),

  setOliverEnabled: (enabled: boolean) =>
    api.put("/integrations/oliver/status", { enabled }),

  disconnectOliver: () => api.delete("/integrations/oliver"),
};

// Transport orders received from Oliver, waiting to become container
// orders. Reviewed by the same people who create container orders.
export const containerOrderIntakeAPI = {
  list: (params: {
    status: "pending" | "accepted" | "declined" | "failed";
    page?: number;
    limit?: number;
    search?: string;
    sort?: IntakeSortKey;
    order?: "asc" | "desc";
    /** A user id or `unassigned`; absent is everyone. */
    handler?: string;
  }) => api.get("/container-order-intakes", { params }),

  counts: () => api.get("/container-order-intakes/counts"),

  get: (intakeId: string) => api.get(`/container-order-intakes/${intakeId}`),

  save: (intakeId: string, draft: unknown, updatedAt?: string) =>
    api.put(`/container-order-intakes/${intakeId}`, { draft, updatedAt }),

  rematch: (intakeId: string) =>
    api.post(`/container-order-intakes/${intakeId}/rematch`),

  /** Read an order that failed to import, again, now. */
  retry: (intakeId: string) =>
    api.post(`/container-order-intakes/${intakeId}/retry`),

  accept: (intakeId: string, updatedAt?: string) =>
    api.post(`/container-order-intakes/${intakeId}/accept`, { updatedAt }),

  decline: (intakeId: string, reason: string) =>
    api.post(`/container-order-intakes/${intakeId}/decline`, { reason }),

  restore: (intakeId: string) =>
    api.post(`/container-order-intakes/${intakeId}/restore`),

  /**
   * Take this order for review, hand it on, or put it back.
   *
   * The most-pressed control on the screen: one click on a row's empty bubble
   * claims the order, which is how two dispatchers stop spending the same
   * afternoon on it. No concurrency token — two people claiming at once is the
   * race this field exists to make visible, and the honest resolution is that
   * the second write wins and both screens then agree whose it is.
   */
  setHandler: (intakeId: string, handlerId: string | null) =>
    api.patch<{ data: { handler: Handler | null }; message: string }>(
      `/container-order-intakes/${intakeId}/handler`,
      { handlerId },
    ),
};

export const suggestionsAPI = {
  getPartners: (params?: any) => api.get("/partners/suggestions", { params }),

  getAddresses: (params?: any) => api.get("/addresses/suggestions", { params }),

  getVehicles: (params?: any) => api.get("/vehicles/suggestions", { params }),

  getUsers: (params?: any) => api.get("/users/suggestions", { params }),
};

export const apiResource = (resource: ResourceType) => ({
  getAll: (params?: any) => api.get(`/${resource}`, { params }),

  getOne: (id: string) => api.get(`/${resource}/${id}`),

  create: <T>(data: T) => api.post(`/${resource}`, data),

  update: <T>(id: string, data: T) => api.put(`/${resource}/${id}`, data),

  delete: (id: string) => api.delete(`/${resource}/${id}`),
});

/**
 * Partner tag colours. Kept apart from the partner resource because the colour
 * is changed on its own, from the partner's header, without the form — see
 * `setPartnerColor` in the partner controller.
 */
export const partnerColorsAPI = {
  /** Which colours partners already hold, and the one a new partner gets. */
  getAll: () => api.get("/partners/colors"),
  set: (id: string, color: string | null) =>
    api.patch(`/partners/${id}/color`, { color }),
};

export const servicesAPI = {
  /**
   * The Client Specific tab: one row per client, with that client's rates
   * already attached. Not `apiResource("services").getAll` with a grouping
   * flag, because the response is a different shape — clients, not services —
   * and pages over clients.
   */
  getClientGroups: (params?: {
    search?: string;
    page?: number;
    limit?: number;
    partnerId?: string;
  }) => api.get("/services/by-client", { params }),
};

export const notificationAPI = {
  getNotifications: (params?: NotificationFilters) =>
    api.get("/notifications", { params }),

  getUnreadNotifications: (params?: { page?: number; limit?: number }) =>
    api.get("/notifications/unread", { params }),

  getUnreadCount: () => api.get("/notifications/unread-count"),

  markAsRead: (notificationId: string) =>
    api.patch(`/notifications/${notificationId}/read`),

  markAllAsRead: () => api.patch("/notifications/mark-all-read"),
};

const createDriverAPI = (apiInstance: any) => ({
  setReadyForPickup: (token: string, latitude: number, longitude: number) =>
    apiInstance.patch(`/driver/shipment/${token}/ready-for-pickup`, {
      latitude,
      longitude,
    }),

  setAwaitingPickup: (token: string, latitude: number, longitude: number) =>
    apiInstance.patch(`/driver/shipment/${token}/awaiting-pickup`, {
      latitude,
      longitude,
    }),

  startJourney: (token: string, latitude: number, longitude: number) =>
    apiInstance.patch(`/driver/shipment/${token}/start`, {
      latitude,
      longitude,
    }),

  completeJourney: (
    token: string,
    latitude: number,
    longitude: number,
    otp?: string,
  ) =>
    apiInstance.patch(`/driver/shipment/${token}/complete`, {
      latitude,
      longitude,
      otp,
    }),

  addService: (token: string, services: ServiceSnapshot[]) =>
    apiInstance.patch(`/driver/shipments/${token}/services`, {
      services,
    }),

  uploadAttachments: (token: string, formData: FormData) =>
    apiInstance.post(`/driver/shipments/${token}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  downloadAttachment: (token: string, attachmentId: string) =>
    apiInstance.get(
      `/driver/shipments/${token}/attachments/${attachmentId}/download`,
      { responseType: "blob" },
    ),

  deleteAttachment: (token: string, attachmentId: string) =>
    apiInstance.delete(
      `/driver/shipments/${token}/attachments/${attachmentId}`,
    ),

  // Signed Documents (driver side) — permanent, immutable. Upload + download only.
  uploadSignedDocuments: (token: string, formData: FormData) =>
    apiInstance.post(
      `/driver/shipments/${token}/signed-documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),
  downloadSignedDocument: (
    token: string,
    documentId: string,
    preview = false,
  ) =>
    apiInstance.get(
      `/driver/shipments/${token}/signed-documents/${documentId}/download`,
      { responseType: "blob", params: preview ? { preview: 1 } : undefined },
    ),

  getShipmentComments: (
    token: string,
    params?: { page?: number; limit?: number },
  ) => apiInstance.get(`/driver/shipments/${token}/comments`, { params }),

  addShipmentComment: (token: string, data: AddCommentPayload) =>
    apiInstance.post(`/driver/shipments/${token}/comments`, data),

  deleteShipmentComment: (token: string, commentId: string) =>
    apiInstance.delete(`/driver/shipments/${token}/comments/${commentId}`),

  fileReclamation: (token: string, formData: FormData) =>
    apiInstance.post(`/driver/shipments/${token}/reclamations`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
});

export const driversAPI = {
  ...createDriverAPI(api),
  getShipmentByID: (token: string) => api.get(`/driver/shipments/${token}`),
  getShipments: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get("/driver/shipments", { params }),
  getGroups: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get("/driver/groups", { params }),
  getGroupById: (groupId: string) => api.get(`/driver/groups/${groupId}`),
};

export const driverPublicAPI = {
  ...createDriverAPI(publicApi),
  getShipmentByID: (token: string) =>
    publicApi.get(`/driver/shipment/${token}`),
};

export const calendarAPI = {
  getCalendarData: (params: {
    start: string;
    end: string;
    tz?: string;
    includeAllVehicles?: boolean;
  }) => api.get<CalendarResponse>("/calendar", { params }),
};

/**
 * ── The container planning board ─────────────────────────────────────────────
 *
 * Separate from `calendarAPI` above, which still serves the shipment gantt. The
 * two screens answer different questions — that one draws bookings on a time
 * axis, this one dispatches containers into ordered slots — and folding them
 * into one client object would suggest a relationship the endpoints do not have.
 *
 * Every write names one container and returns the card (or cards) it changed,
 * so the board can apply the result without refetching the week. The refetch
 * still happens, driven by the `planning:changed` socket signal, because the
 * board is a shared desk — but that is about *other people's* changes, not
 * about your own.
 */
/** Reporting over containers — per-truck work and mileage for a date range. */
export const containerReportAPI = {
  trucks: (params: { from: string; to: string; vehicleId?: string }) =>
    api.get<{ data: TruckReport }>("/container-reports/trucks", { params }),

  /** Every truck the company has, for the report's truck picker. */
  truckOptions: () =>
    api.get<{ data: { trucks: TruckOption[] } }>("/container-reports/truck-options"),
};

export const planningAPI = {
  /**
   * The board, or — with `poolOnly` — just the waiting pool.
   *
   * A search changes the pool and can change nothing else, so it asks the
   * narrower question rather than dragging a week of the fleet back with it.
   * The response is a `PlanningPoolPage`; the caller merges it over the board it
   * already has.
   */
  getBoard: (params: {
    start: string;
    end: string;
    poolLimit?: number;
    poolSearch?: string;
    poolOnly?: boolean;
    /**
     * Narrow the waiting pool to one dispatcher, or to the unclaimed.
     *
     * The pool only. The grid comes back whole whatever this says, because its
     * free-slot counts are read off every placement — a slot that looked free
     * because somebody else's job had been filtered out of it is a slot that
     * gets double-booked. The page draws out-of-scope jobs as small grey tiles
     * instead.
     *
     * Server-side for the same reason `poolHideStarted` is: the pool is capped
     * at `poolLimit` and `poolTotal` counts the uncapped set, so filtering in
     * the page would narrow an already-full page and leave the dispatcher's own
     * containers stranded behind the cap.
     */
    handler?: string;
    /**
     * Leave running containers out of the pool.
     *
     * Server-side rather than a filter over the answer, because the pool is a
     * capped page: on a company that plans from the container's own form, the
     * cap would otherwise fill with running work and the containers waiting for
     * a plan would sit behind it. Ignored while `poolSearch` is set — a search
     * reaches the whole pool.
     */
    poolHideStarted?: boolean;
  }) =>
    api.get<{ data: PlanningBoard | PlanningPoolPage }>("/planning/board", {
      params,
    }),

  /**
   * The same window as points on a map.
   *
   * A separate request from `getBoard`, and a much narrower row: every waiting
   * container there is — uncapped, unlike the board's pool — plus the placed
   * ones whose slots fall in the window. Read on opening the map and refreshed
   * with it, never alongside the board.
   */
  getMap: (params: { start: string; end: string }) =>
    api.get<{ data: PlanningMapPage }>("/planning/map", { params }),

  /**
   * The runs behind a handful of map pins — for the panel, on demand.
   *
   * Batched because one click on a crowded yard selects everything under it.
   * Kept off `getMap` on purpose: a route is a couple of hundred bytes, which
   * is nothing for the container that was clicked and a doubling of the payload
   * for the thousands that were not.
   */
  getMapRoutes: (ids: string[]) =>
    api.get<{ data: { routes: PlanningMapRoute[] } }>("/planning/map/routes", {
      params: { ids: ids.join(",") },
    }),

  /** Place a container in a truck's slot, or move one already placed. */
  assign: (payload: {
    lineId: string;
    vehicleId: string;
    date: string;
    slot: number;
  }) => api.post("/planning/assign", payload),

  /** Take it off the board entirely — back to the waiting pool. */
  unassign: (lineId: string) => api.post("/planning/unassign", { lineId }),

  /**
   * One more slot for a placed container. The truck must be its own — the
   * server refuses any other, so one container never spans two trucks.
   */
  addSlot: (payload: {
    lineId: string;
    vehicleId: string;
    date: string;
    slot: number;
  }) => api.post("/planning/slots/add", payload),

  /** Free one of a container's slots and keep the rest. */
  removeSlot: (payload: { lineId: string; date: string; slot: number }) =>
    api.post("/planning/slots/remove", payload),

  /** The created → planned move: this box is now on the screen in the cab. */
  send: (lineId: string) => api.post("/planning/send", { lineId }),

  /**
   * Take that release back.
   *
   * The only backwards status move in the module, and only while nothing has
   * been driven — the server refuses it the moment a step is stamped.
   */
  unsend: (lineId: string) => api.post("/planning/unsend", { lineId }),

  blockSlot: (payload: {
    vehicleId: string;
    date: string;
    slot: number;
    reason?: string;
  }) => api.post("/planning/blocks", payload),

  releaseSlot: (blockId: string) => api.delete(`/planning/blocks/${blockId}`),
};

export const taskAPI = {
  getTasks: (params?: Record<string, unknown>) => api.get("/tasks", { params }),

  getTask: (id: string) => api.get(`/tasks/${id}`),

  createTask: (data: Record<string, unknown>) => api.post("/tasks", data),

  updateTask: (id: string, data: Record<string, unknown>) =>
    api.put(`/tasks/${id}`, data),

  deleteTask: (id: string) => api.delete(`/tasks/${id}`),

  createShipmentFromTask: (
    taskId: string,
    body?: {
      transport?: Record<string, unknown>;
      clientId?: string | null;
      clientName?: string | null;
    },
  ) => api.post(`/tasks/${taskId}/create-shipment`, body ?? {}),

  createActivity: (taskId: string, data: Record<string, unknown>) =>
    api.post(`/tasks/${taskId}/activities`, data),

  updateActivity: (
    taskId: string,
    activityId: string,
    data: Record<string, unknown>,
  ) => api.put(`/tasks/${taskId}/activities/${activityId}`, data),

  deleteActivity: (taskId: string, activityId: string) =>
    api.delete(`/tasks/${taskId}/activities/${activityId}`),

  getAssignees: () => api.get("/tasks/assignees"),
};

// Works API
export const worksAPI = {
  // Dispatcher (admin / user)
  getWorks: (params?: Record<string, unknown>) => api.get("/works", { params }),

  getWork: (id: string) => api.get(`/works/${id}`),

  createWork: (data: Record<string, unknown>) => api.post("/works", data),

  updateWork: (id: string, data: Record<string, unknown>) =>
    api.put(`/works/${id}`, data),

  deleteWork: (id: string) => api.delete(`/works/${id}`),

  // Dispatcher service management — allowed on completed works too.
  addDispatcherService: (id: string, data: Record<string, unknown>) =>
    api.post(`/works/${id}/services`, data),

  deleteDispatcherService: (id: string, lineId: string) =>
    api.delete(`/works/${id}/services/${lineId}`),

  getCatalog: () => api.get("/works/catalog"),

  // Driver execution
  getDriverWorks: () => api.get("/works/driver/mine"),

  getDriverWork: (id: string) => api.get(`/works/driver/${id}`),

  startWork: (id: string, data?: Record<string, unknown>) =>
    api.post(`/works/driver/${id}/start`, data ?? {}),

  pauseWork: (id: string, data?: Record<string, unknown>) =>
    api.post(`/works/driver/${id}/pause`, data ?? {}),

  resumeWork: (id: string, data?: Record<string, unknown>) =>
    api.post(`/works/driver/${id}/resume`, data ?? {}),

  completeWork: (id: string, data?: Record<string, unknown>) =>
    api.post(`/works/driver/${id}/complete`, data ?? {}),

  roundLoaded: (id: string, data?: Record<string, unknown>) =>
    api.post(`/works/driver/${id}/round-loaded`, data ?? {}),

  roundUnloaded: (id: string, data?: Record<string, unknown>) =>
    api.post(`/works/driver/${id}/round-unloaded`, data ?? {}),

  step: (id: string, data: Record<string, unknown>) =>
    api.post(`/works/driver/${id}/step`, data),

  updateMetric: (id: string, data: Record<string, unknown>) =>
    api.patch(`/works/driver/${id}/metric`, data),

  addService: (id: string, data: Record<string, unknown>) =>
    api.post(`/works/driver/${id}/services`, data),

  updateServiceQuantity: (
    id: string,
    lineId: string,
    data: Record<string, unknown>,
  ) => api.patch(`/works/driver/${id}/services/${lineId}`, data),
};

/**
 * Container lines, as seen by the driver executing them.
 *
 * Mirrors the works driver API rather than the shipment one: a line is only
 * ever executed by a logged-in driver, so there is no public-link variant.
 */
export const containerLineDriverAPI = {
  /**
   * `active` is the driver's to-do list; `completed` is their own history.
   *
   * `limit` is what asks for a page. Omitted, the whole scope comes back — the
   * execution screen reads the active list only to work out which container is
   * next, and a truncated answer to that question would name the wrong box. The
   * list page asks for a page and scrolls for the rest.
   *
   * The response carries `total` and `hasMore` alongside the rows whenever a
   * limit was sent; unpaged, `total` is simply the length and `hasMore` false,
   * so one reader works for both.
   */
  getMine: (
    scope: "active" | "completed" = "active",
    paging?: { page?: number; limit?: number },
  ) =>
    api.get<{
      data: {
        lines: unknown[];
        total: number;
        hasMore: boolean;
        page: number;
      };
    }>("/container-orders/lines/driver/mine", {
      params: { scope, ...(paging ?? {}) },
    }),

  getOne: (lineId: string) =>
    api.get(`/container-orders/lines/driver/${lineId}`),

  /**
   * Record one step of the container's run. Steps must arrive in order.
   *
   * `stopId` says which stop the step belongs to. It is needed because a route
   * can hold several added stops and they all call their two halves
   * `arrived_stop` and `stop_completed` — the step name alone stopped being able
   * to identify a step once routes could vary. Optional on the wire: a
   * three-stop run has one of each name, so an older client that does not send
   * it is still unambiguous.
   */
  step: (
    lineId: string,
    data: {
      step: string;
      stopId?: string;
      /**
       * The container's `updatedAt` as the screen loaded it.
       *
       * Sent so the server can refuse a step recorded against a route the driver
       * is no longer looking at: dispatch can reorder the route or turn a stop
       * off while the phone still shows the old one, and the ordering check
       * cannot see that — it only asks whether the step is next on the route the
       * *server* holds. It also makes a double tap safe, since the second
       * request arrives stale rather than stamping the same position twice.
       */
      updatedAt?: string;
      /**
       * Where the driver was — required, not best-effort.
       *
       * The server refuses a driver-recorded step without it (422). A step
       * from a phone is evidence, and the whole of its value is the pairing:
       * this person, at this time, at this place. Without the third, "collected
       * at 11:58" and "claimed to have collected at 11:58" are the same
       * document. A driver who genuinely cannot get a fix asks the office
       * through `reportGeoBlocked` above.
       */
      latitude: number;
      longitude: number;
      /** The radius the fix is good to, in metres, when the phone stated one. */
      accuracy?: number;
      note?: string;
    },
  ) => api.post(`/container-orders/lines/driver/${lineId}/step`, data),

  /**
   * The box's own number, seal and weights, filled in from the yard.
   *
   * The number is only accepted while the container is still planned — once it
   * starts, it is what the run is about and the server freezes it. The seal and
   * the weights stay open until the container finishes: the weighing happens
   * mid-run, and the seal goes on when the box is closed. The seal is optional
   * throughout, blank included.
   */
  saveDetails: (
    lineId: string,
    data: {
      /** The container as this screen loaded it — see the note on `step`. */
      updatedAt?: string;
      containerNumber?: string;
      sealNumber?: string;
      /**
       * The customs MRN, writable from here for the whole run.
       *
       * The one identifier on a container that never freezes, and deliberately
       * so: a declaration is routinely lodged, amended or reissued at a customs
       * office partway along the route, which is somewhere only the driver is
       * standing. `""` clears it — an MRN entered against the wrong box has to
       * be removable by whoever noticed.
       *
       * `loadStatus` is absent from this shape on purpose. It is a declaration
       * the office makes to the terminal; the driver is shown it so it can be
       * quoted, not revised.
       */
      mrnNumber?: string;
      /**
       * What the load weighs, off the bridge ticket.
       *
       * Writable from here because the weighing happens after loading — stop 2
       * of 3 — so the driver is the person actually handed the figure. `null`
       * clears the declaration and hands the answer back to the packing list;
       * a `0` declares an empty move, which is a different statement.
       *
       * Not to be confused with the VGM below, which is a sum over this and the
       * tare and is therefore the server's alone.
       */
      cargoWeightKg?: number | null;
      // No VGM: the server derives it from the tare and the packing list and
      // ignores anything sent for it. The driver's screen shows the sum.
      maxPayloadKg?: number | null;
      tareKg?: number | null;
      /**
       * Which plate reading this save is answering, when it is answering one.
       *
       * Optional throughout: a driver who typed everything by hand sends none.
       * What it buys is the pairing that measures the reader — a proposal and
       * the value a person actually kept — which no confidence score can give.
       */
      plateReadingId?: string | null;
      /**
       * What is in the box, as the driver found it at the loading bay.
       *
       * The whole list, every time: a row dropped from it is a row removed, and
       * a row carrying an `_id` is an edit to that row rather than a
       * replacement — the server keeps the fields this screen never shows
       * (volume, dimensions, shipping marks) and patches the four it does.
       *
       * Four fields per row and no more. Omit the key entirely to leave the
       * packing list alone, which is what every save that is only about the
       * weights does.
       */
      cargoItems?: Array<{
        _id?: string;
        quantity: number | null;
        type: string;
        weightKg: number | null;
        description: string;
      }>;
    },
  ) => api.patch(`/container-orders/lines/driver/${lineId}/details`, data),

  /**
   * Read the container's door plate from a burst of photographs.
   *
   * Answers with a verdict and a set of suggested values; it writes nothing.
   * Whatever the driver decides to keep goes back through `saveDetails` above,
   * carrying `plateReadingId` so the reading can be paired with what was
   * actually saved — which is the only honest measure of whether the reader was
   * right, and the audit trail for a figure that ends up in a declared mass.
   *
   * The frames are never stored unless the deployment has explicitly switched
   * retention on.
   */
  /**
   * Tell the office a step cannot be geo-stamped from here.
   *
   * Records nothing on the container, deliberately — an endpoint that could
   * advance the run would *be* the override the location requirement exists to
   * avoid. It raises a high-priority notification naming the container, the
   * stop, the step and which of the three failures it was, so the office can
   * record the step from the container's own form, where the event is stamped
   * `source: "system"` with a reason and their name against it.
   */
  reportGeoBlocked: (
    lineId: string,
    data: { reason: "denied" | "unavailable" | "timeout" },
  ) =>
    api.post<{ success: boolean; message: string }>(
      `/container-orders/lines/driver/${lineId}/geo-blocked`,
      data,
    ),

  readPlate: (lineId: string, frames: File[]) => {
    const body = new FormData();
    for (const frame of frames) body.append("frames", frame);
    return api.post<{
      data: {
        readingId: string | null;
        verdict: "green" | "amber" | "red";
        containerNumber: {
          value: string;
          state: "incomplete" | "valid" | "invalid";
          confident: boolean;
          suggestions: string[];
        };
        weights: {
          tareKg?: number | null;
          maxPayloadKg?: number | null;
        };
        /** Which figures were computed from the other two rather than read. */
        derived: string[];
        errors: Array<{ code: string; field: string; message: string }>;
        warnings: Array<{ code: string; field: string; message: string }>;
        /** Only ever present on a green reading. */
        autoFill: Record<string, string | number> | null;
        /**
         * Which of the uploaded frames the figures came from, by position.
         *
         * For deciding which photograph to file as the evidence. Not the
         * sharpest — a crisp shot of the wrong end of a container scores
         * perfectly on focus and reads nothing.
         */
        bestFrame: number | null;
        frames: number;
        elapsedMs: number | null;
      };
    }>(
      `/container-orders/lines/driver/${lineId}/plate`,
      body,
      // The header is not optional here, and its absence is silent.
      //
      // This instance defaults every request to `application/json`, so a
      // FormData body handed to it goes out claiming to be JSON — axios only
      // detects the body type and sets the multipart boundary when no
      // Content-Type is already present. The server's parser then finds no
      // multipart to parse, `req.files` is empty, and the endpoint answers "No
      // photograph was sent" about a request that carried the photograph
      // perfectly well. Every other upload on this client overrides it for the
      // same reason; this one did not, and that was the whole bug.
      multipart,
    );
  },

  /**
   * File documents against the container, from the yard.
   *
   * Uploaded as they are picked rather than held for a save: this screen has no
   * form to submit, and a receipt photographed at the gate should be filed at
   * the gate. Build the body with `attachmentUploadForm`.
   */
  uploadAttachments: (lineId: string, formData: FormData) =>
    api.post<{
      data: {
        attachments: ContainerLineAttachment[];
        /**
         * The container's new version token.
         *
         * Filing a document is a save, so it moves `updatedAt` — and a screen
         * still holding the token it loaded with would have its next save
         * refused as a lost update, telling the driver somebody else had
         * changed the container when the only somebody was them, one tap
         * earlier. Typed rather than reached for, so the next caller cannot
         * quietly forget it.
         */
        updatedAt: string;
      };
    }>(
      `/container-orders/lines/driver/${lineId}/attachments`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),

  downloadAttachment: (lineId: string, attachmentId: string) =>
    api.get(
      `/container-orders/lines/driver/${lineId}/attachments/${attachmentId}/download`,
      { responseType: "blob" },
    ),

  /** Only ever the driver's own uploads; the API refuses the rest. */
  deleteAttachment: (lineId: string, attachmentId: string) =>
    api.delete<{
      data: { attachments: ContainerLineAttachment[]; updatedAt: string };
    }>(`/container-orders/lines/driver/${lineId}/attachments/${attachmentId}`),

  // ── Messages ────────────────────────────────────────────────────────────
  //
  // The cab's half of the container's thread. The office's half is
  // `containerMessageAPI` below; the two speak to the same conversation and
  // differ only in which containers each may reach — the truck's, or the
  // company's.

  /**
   * A page of the thread, newest last.
   *
   * `before` is the cursor a previous page handed back, not a page number: a
   * conversation grows at the end while it is being read, and an offset would
   * shift under the reader and show a message twice.
   */
  listMessages: (lineId: string, params?: { before?: string; limit?: number }) =>
    api.get<{ data: ContainerMessagePage }>(
      `/container-orders/lines/driver/${lineId}/messages`,
      { params },
    ),

  /**
   * Say something — typed, or one of the five buttons.
   *
   * Exactly one of the two: a quick message takes its words from the server's
   * catalogue, so sending both would be two claims about what was said.
   */
  sendMessage: (
    lineId: string,
    data:
      | { body: string; templateKey?: never }
      | { templateKey: ContainerMessageTemplateKey; body?: never },
  ) =>
    api.post<{
      data: { message: ContainerMessage; messaging: ContainerMessaging };
    }>(`/container-orders/lines/driver/${lineId}/messages`, data),

  /**
   * This side has caught up.
   *
   * Its own call rather than a side effect of listing, because a thread is
   * fetched for several reasons and only one of them means somebody read it.
   * Cheap and idempotent when there is nothing outstanding.
   */
  markMessagesRead: (lineId: string) =>
    api.post<{ data: { messaging: ContainerMessaging } }>(
      `/container-orders/lines/driver/${lineId}/messages/read`,
      {},
    ),
};

/**
 * The office's half of a container's thread.
 *
 * Under the order, like every other dispatcher container route: a dispatcher
 * reaches a container through the booking it belongs to, so a mistyped order id
 * answers 404 instead of opening a conversation about another box.
 */
export const containerMessageAPI = {
  list: (
    orderId: string,
    lineId: string,
    params?: { before?: string; limit?: number },
  ) =>
    api.get<{ data: ContainerMessagePage }>(
      `/container-orders/${orderId}/lines/${lineId}/messages`,
      { params },
    ),

  send: (
    orderId: string,
    lineId: string,
    data:
      | { body: string; templateKey?: never }
      | { templateKey: ContainerMessageTemplateKey; body?: never },
  ) =>
    api.post<{
      data: { message: ContainerMessage; messaging: ContainerMessaging };
    }>(`/container-orders/${orderId}/lines/${lineId}/messages`, data),

  markRead: (orderId: string, lineId: string) =>
    api.post<{ data: { messaging: ContainerMessaging } }>(
      `/container-orders/${orderId}/lines/${lineId}/messages/read`,
      {},
    ),
};

/**
 * ── Truck devices ───────────────────────────────────────────────────────────
 *
 * Two audiences in one object, deliberately: the office half manages devices
 * from the Vehicles screen, the device half is what a tablet in a cab calls. They
 * are the same feature seen from either end, and splitting them into two files
 * would put the pairing code's issue and its redemption out of each other's
 * sight.
 */
export const deviceAPI = {
  // ── Office ───────────────────────────────────────────────────────────────

  /** Every device in the fleet, with who is signed in on each. */
  list: () =>
    api.get<{ data: { devices: TruckDevice[] } }>("/devices"),

  /**
   * Register a device against a truck.
   *
   * The pairing code comes back in this response and in no other: only its hash
   * is stored, so this is the one moment it can be read. The screen that asked
   * for it must show it to the admin before navigating away.
   */
  create: (payload: { vehicleId: string; label?: string }) =>
    api.post<{ data: { device: TruckDevice; pairingCode: string } }>(
      "/devices",
      payload,
    ),

  /**
   * Read back the code already outstanding on a device.
   *
   * Reach for this before regenerating: for a device already in service,
   * regenerating takes that truck's screen out of use until the new code is
   * redeemed, which is a steep price for a dialog closed too early. Distinct
   * `code` values separate "already paired" from "cannot be shown", because the
   * two lead to different offers.
   */
  getPairingCode: (deviceId: string) =>
    api.get<{
      data: {
        pairingCode: string;
        expiresAt: string | null;
        expired: boolean;
        truck: string | null;
      };
    }>(`/devices/${deviceId}/pairing-code`),

  /** A fresh code, retiring whatever was outstanding. Also how a tablet is swapped. */
  regeneratePairingCode: (deviceId: string) =>
    api.post<{ data: { device: TruckDevice; pairingCode: string } }>(
      `/devices/${deviceId}/pairing-code`,
    ),

  /** End a shift somebody left open — for the tablet face-down on a seat. */
  signOut: (deviceId: string) =>
    api.post<{ data: { device: TruckDevice } }>(`/devices/${deviceId}/sign-out`),

  /** Take a device out of service. Its secret stops working immediately. */
  revoke: (deviceId: string) => api.delete(`/devices/${deviceId}`),

  // ── The tablet ───────────────────────────────────────────────────────────

  /**
   * Redeem a pairing code. The only call here that carries no device secret,
   * because it is the call that hands one out.
   */
  pair: (code: string) =>
    api.post<{
      data: {
        deviceToken: string;
        device: { _id: string; label?: string; truck: PairedTruck };
      };
    }>("/devices/pair", { code }),

  /**
   * Which truck this screen is, and whether somebody is already on it.
   *
   * Bounded, unlike every other call here. The keypad waits on this before it
   * draws, and axios has no default timeout — so a request that hangs rather
   * than fails (a captive portal, a yard with signal but no route out) would
   * leave a driver looking at a spinner where the keys should be, indefinitely.
   * Eight seconds is far longer than the call ever takes and far shorter than a
   * driver's patience at a gate; on expiry the page shows the keypad with a
   * notice, because signing in never needed this answer.
   */
  context: () =>
    api.get<{
      data: {
        device: { _id: string; label?: string; truck: PairedTruck };
        // No occupant. This route authenticates on the device secret alone, so
        // it must not name a driver to whoever is holding the tablet — see
        // `getDeviceContext`. Occupancy is an office question, answered by
        // `deviceAPI.list()` behind an authenticated dispatcher.
        lockedUntil: string | null;
      };
    }>("/devices/context", { timeout: 8000 }),

  /**
   * Sign a driver in on this truck. Displaces whoever was on the screen.
   *
   * No `refreshToken` in the response, deliberately: it is set as an httpOnly
   * cookie, the same one `/auth/refresh-token` reads and the same one the
   * password login has always used. It used to be returned here and set nowhere,
   * which meant the shift could not renew itself and the driver was dropped back
   * at the keypad the moment the access token expired.
   */
  pinLogin: (pin: string) =>
    api.post<{
      data: {
        accessToken: string;
        user: User;
        truck: PairedTruck;
      };
    }>("/devices/pin-login", { pin }),

  /** End the shift. The pairing survives; only the driver's session goes. */
  pinLogout: () => api.post("/devices/pin-logout"),
};

/**
 * Truck groups — named, overlapping sets of trucks. Membership lives on the
 * group, so nothing here touches the vehicle records other modules read.
 */
export const truckGroupAPI = {
  list: () => api.get<{ data: { groups: TruckGroup[] } }>("/truck-groups"),

  /** Every truck in the fleet, for the "Add trucks" picker. */
  trucks: () =>
    api.get<{ data: { trucks: GroupTruck[] } }>("/truck-groups/trucks"),

  create: (payload: { name: string; vehicleIds?: string[] }) =>
    api.post<{ data: { group: TruckGroup } }>("/truck-groups", payload),

  rename: (groupId: string, payload: { name: string; updatedAt?: string }) =>
    api.put<{ data: { group: TruckGroup } }>(
      `/truck-groups/${groupId}`,
      payload,
    ),

  delete: (groupId: string) => api.delete(`/truck-groups/${groupId}`),

  addTrucks: (groupId: string, vehicleIds: string[]) =>
    api.post<{ data: { group: TruckGroup } }>(
      `/truck-groups/${groupId}/vehicles`,
      { vehicleIds },
    ),

  removeTruck: (groupId: string, vehicleId: string) =>
    api.delete<{ data: { group: TruckGroup } }>(
      `/truck-groups/${groupId}/vehicles/${vehicleId}`,
    ),

  /** Take a truck out of one group and put it in another. */
  moveTruck: (payload: {
    vehicleId: string;
    fromGroupId: string;
    toGroupId: string;
  }) =>
    api.post<{ data: { groups: TruckGroup[] } }>("/truck-groups/move", payload),
};

export default api;
